import { createContext, useContext, useState, useEffect, ReactNode } from 'react';
import { track } from '../lib/analytics';
import { initThemePreview, subscribeThemePreview } from './themePreview';
import { flattenGlobalSections } from '../lib/sectionSettings';

export interface Product {
  id: string;
  store_id: string;
  name: string;
  description: string;
  price: number;
  compare_price?: number;
  stock: number;
  gst_rate: number;
  images?: string[];
  tags?: string[];
  collection_id?: string;
  product_type?: 'physical' | 'digital';
  created_at?: number;
}

export interface Store {
  id: string;
  store_name: string;
  subdomain: string;
  active_theme?: string;
  custom_domain?: string;
  email?: string;
  plan?: string;
  active_apps?: string[];
  currency?: string;
  tax_config?: {
    enabled?: boolean;
    include_tax_in_prices?: boolean;
  };
  settings?: {
    tagline?: string;
    logo_url?: string;
    banner_url?: string;
    brand_color?: string;
    about_text?: string;
    whatsapp?: string;
    instagram?: string;
    [key: string]: any;
  };
}

export interface Collection {
  id: string;
  title: string;
  description: string;
  type: 'manual' | 'automated';
  rules: any[];
  rules_match: 'all' | 'any';
}

export interface CartItem extends Product {
  quantity: number;
}

interface StoreContextType {
  store: Store | null;
  products: Product[];
  collections: Collection[];
  collection_products: { collection_id: string; product_id: string }[];
  storePages: any[];
  cart: CartItem[];
  loading: boolean;
  error: string;
  addToCart: (product: Product, qty: number, openDrawer?: boolean) => void;
  removeFromCart: (productId: string, name?: string) => void;
  updateQty: (productId: string, qty: number, name?: string) => void;
  clearCart: () => void;
  cartTotal: number;
  cartCount: number;
  cartGst: number;
  subdomain: string;
  settings: any;
  affiliateCode: string;
  affiliateSession: string;
  linkTo: (path: string) => string;
  cartDrawerOpen: boolean;
  openCartDrawer: () => void;
  closeCartDrawer: () => void;
  quickViewProduct: Product | null;
  setQuickViewProduct: (product: Product | null) => void;
  sectionOrder: any[];
  setSectionOrder: (order: any[]) => void;
}

export const StoreContext = createContext<StoreContextType | undefined>(undefined);

export const useStoreContext = () => {
  const context = useContext(StoreContext);
  if (!context) {
    throw new Error('useStoreContext must be used within a StoreProvider');
  }
  return context;
};

function detectSubdomain(): string | null {
  const hostname = window.location.hostname;

  // Reserved subdomains that are NOT stores
  const PLATFORM_HOSTS = new Set([
    'localhost',
    'store.dilzee.com',
    'dilzee.com',
    'www.dilzee.com',
    'dashboard.dilzee.com',
    'admin.dilzee.com',
    'api.dilzee.com',
  ]);

  // Method 1: Hostname-based detection (adv.dilzee.com → 'adv')
  // This is the primary method for production
  if (
    hostname.endsWith('.dilzee.com') &&
    !PLATFORM_HOSTS.has(hostname)
  ) {
    const parts = hostname.split('.');
    if (parts.length === 3) {
      const sub = parts[0].toLowerCase();
      if (sub && sub !== 'www' && sub !== 'api') {
        if (sub.startsWith('theme-')) {
          return 'adv'; // Fallback to adv store for demo data
        }
        return sub;
      }
    }
  }

  // Method 2: Custom domain (malikstore.com → look up by domain)
  // When a merchant has their own domain, the hostname won't contain dilzee.com
  // The Worker sets X-Store-Subdomain header — but headers aren't readable in SPA
  // Instead the Worker proxies to store.dilzee.com and we read from query param
  // which the Worker injects, OR we use the custom domain lookup API
  if (
    !hostname.includes('dilzee.com') &&
    hostname !== 'localhost' &&
    !hostname.includes('127.0.0.1') &&
    !hostname.includes('pages.dev')
  ) {
    // This is a custom domain — subdomain will come from query param
    // The Worker should have injected ?store= when proxying
  }

  // Method 3: Query parameter fallback (?store=adv)
  // Used during development, in previews, and as fallback
  const params = new URLSearchParams(window.location.search);
  const storeParam = params.get('store');
  if (storeParam) {
    sessionStorage.setItem('dilzee_subdomain', storeParam);
    if (storeParam.startsWith('theme-')) {
      return 'adv'; // Fallback to adv store for demo data
    }
    return storeParam;
  }
  
  const cached = sessionStorage.getItem('dilzee_subdomain');
  if (cached) return cached;

  // Method 4: Check for X-Store-Subdomain in a meta tag
  // Some Workers inject store info via HTML meta tags
  const metaTag = document.querySelector('meta[name="x-store-subdomain"]');
  if (metaTag) return metaTag.getAttribute('content');

  // Fallback for local testing
  if (hostname === 'localhost' || hostname === '127.0.0.1') return 'test';

  return null;
}

// Helper to check if current URL is a theme preview
export function getPreviewThemeId(): string | null {
  const hostname = window.location.hostname;
  if (hostname.startsWith('theme-') && hostname.includes('dilzee.com')) {
    return hostname.split('.')[0].replace('theme-', '');
  }
  const params = new URLSearchParams(window.location.search);
  const storeParam = params.get('store');
  if (storeParam && storeParam.startsWith('theme-')) {
    return storeParam.replace('theme-', '');
  }
  return null;
}

const FONT_URLS: Record<string, string> = {
  modern: '',
  elegant: 'https://fonts.googleapis.com/css2?family=Playfair+Display:wght@400;600;700&family=Lato:wght@300;400;700&display=swap',
  minimal: 'https://fonts.googleapis.com/css2?family=DM+Sans:wght@300;400;500;600&display=swap',
  bold: 'https://fonts.googleapis.com/css2?family=Syne:wght@600;700;800&family=Nunito:wght@300;400;600&display=swap',
  classic: 'https://fonts.googleapis.com/css2?family=Merriweather:wght@400;700&family=Source+Sans+Pro:wght@300;400;600&display=swap',
  luxury: 'https://fonts.googleapis.com/css2?family=Cormorant+Garamond:wght@400;600;700&family=Montserrat:wght@300;400;500&display=swap',
  corporate: 'https://fonts.googleapis.com/css2?family=Montserrat:wght@600;700&family=Open+Sans:wght@400;500&display=swap',
  playful: 'https://fonts.googleapis.com/css2?family=Quicksand:wght@500;600;700&family=Montserrat:wght@400;500&display=swap',
  sharp: 'https://fonts.googleapis.com/css2?family=Jost:wght@500;600;700&family=Roboto:wght@400;500&display=swap',
};

const FONT_FAMILIES: Record<string, { heading: string; body: string }> = {
  modern: { heading: 'Inter, sans-serif', body: 'Inter, sans-serif' },
  elegant: { heading: "'Playfair Display', serif", body: "'Lato', sans-serif" },
  minimal: { heading: "'DM Sans', sans-serif", body: "'DM Sans', sans-serif" },
  bold: { heading: "'Syne', sans-serif", body: "'Nunito', sans-serif" },
  classic: { heading: "'Merriweather', serif", body: "'Source Sans Pro', sans-serif" },
  luxury: { heading: "'Cormorant Garamond', serif", body: "'Montserrat', sans-serif" },
  corporate: { heading: "'Montserrat', sans-serif", body: "'Open Sans', sans-serif" },
  playful: { heading: "'Quicksand', sans-serif", body: "'Montserrat', sans-serif" },
  sharp: { heading: "'Jost', sans-serif", body: "'Roboto', sans-serif" },
};

// Google Fonts URLs for premium themes, using the existing font loader. Extend
// per theme; theme apps call ensureThemeFonts(themeId) on mount.
const THEME_FONTS: Record<string, string> = {
  'neo-brutalist': 'https://fonts.googleapis.com/css2?family=Space+Grotesk:wght@500;700;900&family=IBM+Plex+Mono:wght@400;600&display=swap',
  'y2k-cyber-retro': 'https://fonts.googleapis.com/css2?family=Press+Start+2P&family=VT323&display=swap',
  'heritage-vintage': 'https://fonts.googleapis.com/css2?family=Playfair+Display:wght@400;700;900&family=EB+Garamond:ital,wght@0,400;0,500;0,600;1,400&display=swap',
  'expressive-minimalist': 'https://fonts.googleapis.com/css2?family=DM+Sans:wght@400;500;600&family=Instrument+Serif:ital@0;1&display=swap',
  'organic-tactile': 'https://fonts.googleapis.com/css2?family=Nunito:wght@400;600;700;800&family=Fraunces:ital,opsz,wght,SOFT,WONK@0,9..144,300..700,0..100,0..1;1,9..144,300..700,0..100,0..1&display=swap',
  'playful-3d': 'https://fonts.googleapis.com/css2?family=Baloo+2:wght@600;700;800&family=Outfit:wght@400;500;600;700;800&display=swap',
};

/** Injects a theme's Google Fonts stylesheet once, via the same loader used by applySettingsToDOM. */
export function ensureThemeFonts(themeId: string) {
  const href = THEME_FONTS[themeId];
  if (href && !document.querySelector(`link[href="${href}"]`)) {
    const link = document.createElement('link');
    link.rel = 'stylesheet';
    link.href = href;
    document.head.appendChild(link);
  }
}

/** Themes that render their own cart UI — the global drawer must not auto-open for them. */
export const THEMES_WITH_CUSTOM_CART_UI = new Set<string>(['maison', 'neo-brutalist', 'y2k-cyber-retro', 'heritage-vintage', 'expressive-minimalist', 'organic-tactile', 'playful-3d']);

// Mock catalog used ONLY by the Live Theme Editor preview iframe (preview_token
// + embedded). Ensures the /products/preview-product route renders a realistic
// product template and the cart/checkout templates get a seeded cart instead of
// an empty state. Never touches real store state or localStorage.
const MOCK_PREVIEW_PRODUCTS: Product[] = [
  {
    id: 'preview-product',
    store_id: 'preview',
    name: 'Sample Sofa',
    description: 'A sculptural, endlessly comfortable sofa upholstered in bouclé. Built for slow evenings — deep seats, plush feather-blend cushions, and a solid ash frame. __metadata__:{"features":"Bouclé upholstery · Solid ash frame · Feather-blend cushions\\nRemovable, machine-washable covers · Hand-finished in small batches","specs":"Width 210cm · Depth 95cm · Height 80cm\\nSeat depth 62cm · Weight 42kg · Assembly: legs only\\nMaterials: 65% recycled wool bouclé, 35% linen","warranty":"10-year frame warranty · 2-year upholstery warranty"}',
    price: 999,
    compare_price: 1299,
    stock: 10,
    gst_rate: 18,
    tags: ['sofa', 'living-room', 'featured'],
    product_type: 'physical',
    images: [
      'https://images.unsplash.com/photo-1555041469-a586c61ea9bc?w=1200&q=80',
      'https://images.unsplash.com/photo-1493663284031-b7e3aefcae8e?w=1200&q=80',
      'https://images.unsplash.com/photo-1567016432779-094069958ea5?w=1200&q=80',
    ],
  },
  {
    id: 'preview-product-2',
    store_id: 'preview',
    name: 'Aurora Floor Lamp',
    description: 'A softly glowing arc lamp that fills a corner with warm, diffused light. Dimmer included, brass details throughout.',
    price: 249,
    compare_price: 320,
    stock: 20,
    gst_rate: 18,
    tags: ['lamp', 'lighting', 'featured'],
    product_type: 'physical',
    images: ['https://images.unsplash.com/photo-1507473885765-e6ed057f782c?w=1200&q=80'],
  },
];

function getLuminance(hex: string): number {
  if (!hex) return 1;
  const cleanHex = hex.replace('#', '');
  let r = 255, g = 255, b = 255;
  if (cleanHex.length === 3) {
    r = parseInt(cleanHex.substring(0, 1).repeat(2), 16);
    g = parseInt(cleanHex.substring(1, 2).repeat(2), 16);
    b = parseInt(cleanHex.substring(2, 3).repeat(2), 16);
  } else if (cleanHex.length === 6) {
    r = parseInt(cleanHex.substring(0, 2), 16);
    g = parseInt(cleanHex.substring(2, 4), 16);
    b = parseInt(cleanHex.substring(4, 6), 16);
  }
  if (isNaN(r) || isNaN(g) || isNaN(b)) return 1;
  return (0.299 * r + 0.587 * g + 0.114 * b) / 255;
}

function getTextColorMuted(hex: string): string {
  if (!hex) return 'rgba(107, 114, 128, 1)';
  const cleanHex = hex.replace('#', '');
  let r = 26, g = 26, b = 46;
  if (cleanHex.length === 3) {
    r = parseInt(cleanHex.substring(0, 1).repeat(2), 16);
    g = parseInt(cleanHex.substring(1, 2).repeat(2), 16);
    b = parseInt(cleanHex.substring(2, 3).repeat(2), 16);
  } else if (cleanHex.length === 6) {
    r = parseInt(cleanHex.substring(0, 2), 16);
    g = parseInt(cleanHex.substring(2, 4), 16);
    b = parseInt(cleanHex.substring(4, 6), 16);
  }
  if (isNaN(r) || isNaN(g) || isNaN(b)) return 'rgba(107, 114, 128, 1)';
  return `rgba(${r}, ${g}, ${b}, 0.6)`;
}

function getAccentLight(hex: string): string {
  if (!hex) return 'rgba(253, 60, 2, 0.05)';
  const cleanHex = hex.replace('#', '');
  let r = 253, g = 60, b = 2;
  if (cleanHex.length === 3) {
    r = parseInt(cleanHex.substring(0, 1).repeat(2), 16);
    g = parseInt(cleanHex.substring(1, 2).repeat(2), 16);
    b = parseInt(cleanHex.substring(2, 3).repeat(2), 16);
  } else if (cleanHex.length === 6) {
    r = parseInt(cleanHex.substring(0, 2), 16);
    g = parseInt(cleanHex.substring(2, 4), 16);
    b = parseInt(cleanHex.substring(4, 6), 16);
  }
  if (isNaN(r) || isNaN(g) || isNaN(b)) return 'rgba(253, 60, 2, 0.05)';
  return `rgba(${r}, ${g}, ${b}, 0.1)`;
}

function getContrast(a: string, b: string): number {
  const la = getLuminance(a), lb = getLuminance(b);
  const lighter = Math.max(la, lb), darker = Math.min(la, lb);
  return (lighter + 0.05) / (darker + 0.05);
}

function ensureReadableText(text: string, bg?: string): string {
  if (!bg) return text;
  if (getContrast(text, bg) >= 3) return text;
  return getLuminance(bg) < 0.5 ? '#f5f5f5' : '#111111';
}

function autoTextForBg(bg?: string): string | undefined {
  if (!bg) return undefined;
  return getLuminance(bg) < 0.5 ? '#f5f5f5' : '#1a1a2e';
}

function adjustBgColor(hex: string, isDark: boolean): { surface: string; surfaceCard: string; border: string } {
  if (!hex) return { surface: '#f8f8f8', surfaceCard: '#ffffff', border: '#e8e8e8' };
  const cleanHex = hex.replace('#', '');
  let r = 255, g = 255, b = 255;
  if (cleanHex.length === 3) {
    r = parseInt(cleanHex.substring(0, 1).repeat(2), 16);
    g = parseInt(cleanHex.substring(1, 2).repeat(2), 16);
    b = parseInt(cleanHex.substring(2, 3).repeat(2), 16);
  } else if (cleanHex.length === 6) {
    r = parseInt(cleanHex.substring(0, 2), 16);
    g = parseInt(cleanHex.substring(2, 4), 16);
    b = parseInt(cleanHex.substring(4, 6), 16);
  }
  if (isDark) {
    const sr = Math.min(255, r + 10);
    const sg = Math.min(255, g + 10);
    const sb = Math.min(255, b + 10);
    const cr = Math.min(255, r + 18);
    const cg = Math.min(255, g + 18);
    const cb = Math.min(255, b + 18);
    return {
      surface: `#${sr.toString(16).padStart(2, '0')}${sg.toString(16).padStart(2, '0')}${sb.toString(16).padStart(2, '0')}`,
      surfaceCard: `#${cr.toString(16).padStart(2, '0')}${cg.toString(16).padStart(2, '0')}${cb.toString(16).padStart(2, '0')}`,
      border: 'rgba(255, 255, 255, 0.12)'
    };
  } else {
    const sr = Math.max(0, r - 8);
    const sg = Math.max(0, g - 8);
    const sb = Math.max(0, b - 8);
    return {
      surface: `#${sr.toString(16).padStart(2, '0')}${sg.toString(16).padStart(2, '0')}${sb.toString(16).padStart(2, '0')}`,
      surfaceCard: `#${r.toString(16).padStart(2, '0')}${g.toString(16).padStart(2, '0')}${b.toString(16).padStart(2, '0')}`,
      border: 'rgba(0, 0, 0, 0.08)'
    };
  }
}

function applySettingsToDOM(settings: any) {
  const root = document.documentElement;

  // Map universal settings to legacy keys for backward compatibility
  if (settings.similar_products_heading && !settings.related_heading) {
    settings.related_heading = settings.similar_products_heading;
  }
  if (settings.website_bg_color && !settings.section_surface) {
    settings.section_surface = settings.website_bg_color;
  }
  // Map universal upsell cart setting
  if (settings.upsell_cart_enabled !== undefined && settings.cart_upsell_enabled === undefined) {
    settings.cart_upsell_enabled = settings.upsell_cart_enabled;
  }
  // Map universal header settings
  if (settings.header_logo_position && !settings.logo_alignment) {
    settings.logo_alignment = settings.header_logo_position;
  }

  if (settings.brand_color) {
    root.style.setProperty('--accent', settings.brand_color);
    const isBrandLight = getLuminance(settings.brand_color) >= 0.5;
    root.style.setProperty('--accent-contrast', isBrandLight ? '#121212' : '#ffffff');
    root.style.setProperty('--accent-light', getAccentLight(settings.brand_color));
  }

  // Universal: website_bg_color maps to --section-surface (section background)
  if (settings.website_bg_color) {
    root.style.setProperty('--section-surface', settings.website_bg_color);
  }

  const bodyBg = settings.website_bg_color || settings.body_bg;

  const effectiveText = settings.text_color
    ? ensureReadableText(settings.text_color, bodyBg)
    : autoTextForBg(bodyBg);
  if (effectiveText) {
    root.style.setProperty('--text', effectiveText);
    root.style.setProperty('--text-muted', getTextColorMuted(effectiveText));
  }
  if (bodyBg) {
    const isBgDark = getLuminance(bodyBg) < 0.5;
    root.style.setProperty('--bg', bodyBg);
    const { surface, surfaceCard, border } = adjustBgColor(bodyBg, isBgDark);
    root.style.setProperty('--surface', surface);
    root.style.setProperty('--surface-card', surfaceCard);
    root.style.setProperty('--border', border);
  }

  const fontPair = settings.font_pair || 'modern';
  const fonts = FONT_FAMILIES[fontPair];
  if (fonts) {
    root.style.setProperty('--font-heading', fonts.heading);
    root.style.setProperty('--font-body', fonts.body);
  }

  // Premium font engine
  if (settings.font_family) {
    root.style.setProperty('--font-body', settings.font_family);
    root.style.setProperty('--font-heading', settings.font_family);
  }
  if (settings.font_url && !document.querySelector(`link[href="${settings.font_url}"]`)) {
    const link = document.createElement('link');
    link.rel = 'stylesheet'; link.href = settings.font_url;
    document.head.appendChild(link);
  }

  const buttonRadius: Record<string, string> = { rounded: '8px', sharp: '0px', pill: '99px' };
  if (settings.button_style) root.style.setProperty('--button-radius', buttonRadius[settings.button_style] || '8px');

  // New CSS variables for granular controls
  if (settings.logo_size) root.style.setProperty('--logo-size', `${settings.logo_size}px`);
  if (settings.product_title_size) root.style.setProperty('--product-title-size', `${settings.product_title_size}px`);
  if (settings.product_title_weight) root.style.setProperty('--product-title-weight', settings.product_title_weight);
  if (settings.product_title_spacing) root.style.setProperty('--product-title-spacing', `${settings.product_title_spacing}px`);
  if (settings.product_grid_gap) root.style.setProperty('--product-grid-gap', `${settings.product_grid_gap}px`);
  if (settings.product_card_radius !== undefined) root.style.setProperty('--product-card-radius', `${settings.product_card_radius}px`);
  if (settings.product_card_border !== undefined) root.style.setProperty('--product-card-border', `${settings.product_card_border}px`);
  if (settings.section_padding_y) root.style.setProperty('--section-padding-y', `${settings.section_padding_y}px`);
  if (settings.hero_height) root.style.setProperty('--hero-height', `${settings.hero_height}px`);
  if (settings.nav_height) root.style.setProperty('--nav-height', `${settings.nav_height}px`);
  if (settings.announcement_font_size) root.style.setProperty('--announcement-font-size', `${settings.announcement_font_size}px`);
  if (settings.logo_size) root.style.setProperty('--logo-size', `${settings.logo_size}px`);
  if (settings.header_icon_color) root.style.setProperty('--header-icon-color', settings.header_icon_color);
  if (settings.header_menu_color) root.style.setProperty('--header-menu-color', settings.header_menu_color);
  if (settings.header_bg) root.style.setProperty('--header-bg', settings.header_bg);
  if (settings.body_bg) root.style.setProperty('--bg', settings.body_bg);
  if (settings.heading_base_size) root.style.setProperty('--heading-base-size', `${settings.heading_base_size}px`);
  if (settings.body_base_size) root.style.setProperty('--body-base-size', `${settings.body_base_size}px`);
  if (settings.body_line_height) root.style.setProperty('--body-line-height', settings.body_line_height);
  if (settings.heading_weight) root.style.setProperty('--heading-weight', settings.heading_weight);
  if (settings.heading_spacing) root.style.setProperty('--heading-spacing', `${settings.heading_spacing}px`);

  // Collection page settings
  if (settings.collection_columns_desktop) root.style.setProperty('--collection-cols-desktop', String(settings.collection_columns_desktop));
  if (settings.collection_columns_mobile) root.style.setProperty('--collection-cols-mobile', String(settings.collection_columns_mobile));

  // Cart/checkout settings
  if (settings.checkout_accent_color) root.style.setProperty('--checkout-accent', settings.checkout_accent_color);

  // Footer settings
  if (settings.footer_bg) root.style.setProperty('--footer-bg', settings.footer_bg);
  if (settings.footer_text_color) root.style.setProperty('--footer-text', settings.footer_text_color);

  // Buy button
  if (settings.buy_btn_height) root.style.setProperty('--buy-btn-height', `${settings.buy_btn_height}px`);

  // Load Google Font if needed
  const fontUrl = FONT_URLS[fontPair];
  if (fontUrl && !document.querySelector(`link[href="${fontUrl}"]`)) {
    const link = document.createElement('link');
    link.rel = 'stylesheet';
    link.href = fontUrl;
    document.head.appendChild(link);
  }
}

const applySettings = (settings: any) => {
  applySettingsToDOM(settings);
};

export const StoreProvider = ({ children }: { children: ReactNode }) => {
  const [subdomain, setSubdomain] = useState<string>('');

  const [store, setStore] = useState<Store | null>(null);
  const [products, setProducts] = useState<Product[]>([]);
  const [collections, setCollections] = useState<Collection[]>([]);
  const [collectionProducts, setCollectionProducts] = useState<{ collection_id: string; product_id: string }[]>([]);
  const [storePages, setStorePages] = useState<any[]>([]);
  const [cart, setCart] = useState<CartItem[]>([]);
  const [cartLoaded, setCartLoaded] = useState(false);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [cartDrawerOpen, setCartDrawerOpen] = useState(false);
  const [quickViewProduct, setQuickViewProduct] = useState<Product | null>(null);
  const [sectionOrder, setSectionOrder] = useState<any[]>([]);
  // Mock cart used ONLY inside the Live Theme Editor preview iframe. Seeded with
  // MOCK_PREVIEW_PRODUCTS so /cart and /checkout render content instead of empty
  // states; every cart operation routes through the displayed cart while active.
  const [mockCart, setMockCart] = useState<CartItem[]>([]);
  const [mockPreviewActive, setMockPreviewActive] = useState(false);

  const openCartDrawer = () => setCartDrawerOpen(true);
  const closeCartDrawer = () => setCartDrawerOpen(false);

  useEffect(() => {
    // Affiliate tracking
    const params = new URLSearchParams(window.location.search);
    const affCode = params.get('aff');
    const affSession = params.get('aff_session');
    if (affCode) {
      sessionStorage.setItem('dilzee_aff_code', affCode);
      sessionStorage.setItem('dilzee_aff_session', affSession || '');
    }

    const fetchStoreData = async () => {
      const detectedSubdomain = detectSubdomain();

      if (!detectedSubdomain) {
        setError('No store specified');
        setLoading(false);
        return;
      }

      setSubdomain(detectedSubdomain);

      // Inject dynamic manifest after hydration to avoid blocking network/main thread
      setTimeout(() => {
        const API_BASE = import.meta.env.VITE_API_URL || 'https://api.dilzee.com';
        let manifestLink = document.querySelector('link[rel="manifest"]');
        if (!manifestLink) {
          manifestLink = document.createElement('link');
          manifestLink.setAttribute('rel', 'manifest');
          document.head.appendChild(manifestLink);
        }
        manifestLink.setAttribute('crossorigin', 'use-credentials');
        manifestLink.setAttribute('href', `${API_BASE}/public/${detectedSubdomain}/manifest.json?origin=${encodeURIComponent(window.location.origin)}&v=2`);
      }, 1000);

      if (window.location.href.includes('demo=luxe') || window.location.pathname.includes('/luxe-demo')) {
        setLoading(false);
        return;
      }

      // Track the visit for ALL stores — including the edge-hydrated path below.
      // (Previously this only ran on the fetch path, so injected stores never
      // reported page_view to analytics — killing live view / visit metrics.)
      track(detectedSubdomain, 'page_view', { path: window.location.pathname });

      // --- EDGE HYDRATION ---
      const injectedData = (window as any).__STORE_DATA__;
      if (injectedData) {
        const data = injectedData;
        const parsedSettings = typeof data.store.settings === 'string' 
          ? JSON.parse(data.store.settings || '{}') 
          : (data.store.settings || {});
        
        // Flatten global Header/Footer section entry settings to the top level
        // so every theme's Header/Footer sees them (live editor + production).
        const effectiveSettings = flattenGlobalSections(parsedSettings);
        let storeData = { ...data.store, settings: effectiveSettings };
        const previewTheme = getPreviewThemeId();
        if (previewTheme) {
          storeData.active_theme = previewTheme;
        }

        setStore(storeData);
        setProducts(data.products || []);
        setCollections(data.collections || []);
        setCollectionProducts(data.collection_products || []);
        // Note: Pages aren't injected by rewriter yet, but we can add them to worker if needed.
        // For now, hydrate the core data instantly.
        applySettings(effectiveSettings);
        if (effectiveSettings.section_order) setSectionOrder((effectiveSettings as any).section_order);
        setLoading(false);
        return;
      }

      try {
        const API_BASE = import.meta.env.VITE_API_URL || 'https://api.dilzee.com';
        const response = await fetch(`${API_BASE}/public/${detectedSubdomain}`);
        if (!response.ok) {
          throw new Error('Store not found');
        }
        const data = await response.json();
        
        let fetchedPages = [];
        try {
          const pagesRes = await fetch(`${API_BASE}/public/${detectedSubdomain}/pages`);
          if (pagesRes.ok) {
            const pd = await pagesRes.json();
            fetchedPages = pd.pages || [];
          }
        } catch (e) {}

        const parsedSettings = typeof data.store.settings === 'string' 
          ? JSON.parse(data.store.settings || '{}') 
          : (data.store.settings || {});
        
        const effectiveSettings = flattenGlobalSections(parsedSettings);
        let storeData = { ...data.store, settings: effectiveSettings };
        const previewTheme = getPreviewThemeId();
        if (previewTheme) {
          storeData.active_theme = previewTheme;
        }

        setStore(storeData);
        setProducts(data.products || []);
        setCollections(data.collections || []);
        setCollectionProducts(data.collection_products || []);
        setStorePages(fetchedPages);
        
        applySettings(effectiveSettings);
        if (effectiveSettings.section_order) setSectionOrder((effectiveSettings as any).section_order);
      } catch (err: any) {
        setError(err.message);
      } finally {
        setLoading(false);
      }
    };

    fetchStoreData();
  }, []);

  // --- LIVE THEME EDITOR MOCK DATA ---
  // Only inside the embedded preview (window.self !== window.top) with a
  // preview_token. Injects the sample catalog so /products/preview-product
  // always renders, and seeds /cart + /checkout with a realistic cart. All of
  // it lives in mockCart, so the merchant's real cart / localStorage is never
  // touched while the editor preview is active.
  useEffect(() => {
    const isEditorPreview = window.self !== window.top && new URLSearchParams(window.location.search).has('preview_token');
    if (!isEditorPreview) return;
    setMockPreviewActive(true);
    setProducts(prev => {
      const existing = new Set(prev.map(p => p.id));
      const additions = MOCK_PREVIEW_PRODUCTS.filter(p => !existing.has(p.id));
      return additions.length ? [...prev, ...additions] : prev;
    });
  }, []);

  useEffect(() => {
    if (!mockPreviewActive || loading) return;
    const path = window.location.pathname;
    if (path === '/cart' || path === '/checkout') {
      const sofa = MOCK_PREVIEW_PRODUCTS[0];
      const lamp = MOCK_PREVIEW_PRODUCTS[1];
      if (!sofa || !lamp) return;
      setMockCart([
        { ...sofa, quantity: 1 },
        { ...lamp, quantity: 1 },
      ]);
    }
  }, [mockPreviewActive, loading]);

  // Add inside StoreContext provider, after initial data load:
  useEffect(() => {
    // Live-editor bridge. Applies BOTH postMessage contracts used by the Theme
    // Editor without depending on the (HMAC-gated) themePreview overlay, so
    // Header/Footer + global settings updates always reach React state.
    const applyLiveSettings = (raw: any, sectionConfigs?: any, cssVars?: Record<string, string>) => {
      if (!raw || typeof raw !== 'object') return;

      let merged = flattenGlobalSections(raw);

      // Legacy contract detail: the editor can also ship per-section configs
      // (either a section array or a { [sectionId]: { settings } } record).
      // Merge those over the top-level view as an extra authoritative source.
      if (Array.isArray(sectionConfigs)) {
        for (const sec of sectionConfigs) {
          if (sec && typeof sec === 'object' && (sec as any).settings) {
            merged = { ...merged, ...(sec as any).settings };
          }
        }
        setSectionOrder(sectionConfigs);
      } else if (sectionConfigs && typeof sectionConfigs === 'object') {
        for (const sec of Object.values(sectionConfigs as any)) {
          if (sec && typeof sec === 'object' && (sec as any).settings) {
            merged = { ...merged, ...(sec as any).settings };
          }
        }
        setSectionOrder(Object.keys(sectionConfigs as any));
      }

      // Immediate CSS feedback for global color/layout vars
      if (cssVars && typeof cssVars === 'object') {
        for (const [k, v] of Object.entries(cssVars)) {
          document.documentElement.style.setProperty(k.startsWith('--') ? k : `--${k}`, String(v));
        }
      }

      setStore(prev => {
        const base = prev || { id: 'temp', store_name: '', subdomain: '', settings: {} };
        // Merge over existing settings so partial payloads never clobber
        // unrelated keys (store identity, theme defaults, etc.).
        return { ...base, settings: { ...(base.settings || {}), ...merged } as unknown as Store['settings'] };
      });

      applySettingsToDOM(merged);
    };

    const handleEditorMessage = (event: MessageEvent) => {
      // Security: allow the dilzee dashboard, any pages.dev subdomain, and localhost
      const origin = event.origin;
      const isAllowed = 
        origin.endsWith('.pages.dev') || 
        origin.startsWith('http://localhost:') || 
        origin.startsWith('http://127.0.0.1:');
      
      if (!isAllowed) return;

      const data = event.data as any;
      if (!data || typeof data !== 'object') return;

      if (data.type === 'DILZEE_THEME_PREVIEW') {
        const config = data.config;
        if (config && typeof config === 'object') {
          applyLiveSettings(config.settings, config.sectionConfigs, config.cssVars);
        }
      }

      if (data.type === 'DILZEE_SETTINGS_UPDATE') {
        applyLiveSettings(data.settings, data.sectionConfigs || data.section_order);
      }

      if (data.type === 'DILZEE_VIEWPORT') {
        // Editor is toggling mobile/desktop view
        document.documentElement.setAttribute('data-viewport', data.viewport);
      }

      if (data.type === 'DILZEE_ACTIVE_SECTION') {
        // Find section element and scroll into view
        const sectionId = data.sectionId;
        const el = document.querySelector(`[data-section-id="${sectionId}"]`);
        if (el) {
          el.scrollIntoView({ behavior: 'smooth', block: 'center' });
          // We apply a class to the body or element
          document.querySelectorAll('[data-section-id]').forEach(s => s.removeAttribute('data-active'));
          el.setAttribute('data-active', 'true');
        }
      }
    };

    // Check if loaded inside an iframe (editor mode)
    const isEditorMode = window.self !== window.top;
    if (isEditorMode) document.body.setAttribute('data-editor', 'true');

    window.addEventListener('message', handleEditorMessage);
    return () => window.removeEventListener('message', handleEditorMessage);
  }, []);

  // Phase 4: Theme preview overlay (HMAC-signed token, no API, no persistence)
  useEffect(() => {
    initThemePreview();
    return subscribeThemePreview((cfg) => {
      // Merge preview settings into React state so themes that consume
      // settings keys directly (e.g. settings?.neo_accent_yellow) re-render.
      if (cfg?.settings) {
        const merged = flattenGlobalSections(cfg.settings);
        setStore((prev) => {
          const base = prev || { id: 'temp', store_name: '', subdomain: '', settings: {} };
          return {
            ...base,
            settings: { ...(base.settings || {}), ...merged } as unknown as Store['settings'],
          };
        });
        applySettingsToDOM(merged);
      }
      if (cfg?.sectionConfigs) {
        // sectionConfigs is the full section array from the editor (element order).
        if (Array.isArray(cfg.sectionConfigs)) setSectionOrder(cfg.sectionConfigs);
        else setSectionOrder(Object.keys(cfg.sectionConfigs));
      }
      if (cfg?.productGridColumns) {
        document.documentElement.style.setProperty('--collection-cols-desktop', String(cfg.productGridColumns));
      }
    });
  }, []);

  // Notify editor that storefront is ready
  useEffect(() => {
    if (store) {
      window.parent.postMessage({ type: 'DILZEE_STOREFRONT_READY', subdomain }, '*');
    }
  }, [store, subdomain]);

  // Load cart from local storage once the real subdomain is known.
  // (Must not run at mount with subdomain='' — that would miss the saved cart
  //  and let the save effect below overwrite it with an empty array.)
  useEffect(() => {
    if (!subdomain) return;
    const savedCart = localStorage.getItem(`dilzee_cart_${subdomain}`);
    if (savedCart) {
      try {
        const parsed = JSON.parse(savedCart);
        if (Array.isArray(parsed)) setCart(parsed);
      } catch (e) {
        console.error("Failed to parse cart", e);
      }
    }
    setCartLoaded(true);
  }, [subdomain]);

  // Persist cart, but only after it has been hydrated so a refresh never
  // overwrites the stored cart with an empty one before load completes.
  useEffect(() => {
    if (!loading && cartLoaded && subdomain) {
      localStorage.setItem(`dilzee_cart_${subdomain}`, JSON.stringify(cart));
    }
  }, [cart, loading, cartLoaded, subdomain]);

  const displayCart = mockPreviewActive ? mockCart : cart;

  const applyCart = (updater: (prev: CartItem[]) => CartItem[]) => {
    if (mockPreviewActive) setMockCart(updater);
    else setCart(updater);
  };

  const addToCart = (product: Product, qty: number, openDrawer = true) => {
    applyCart(prev => {
      const existing = prev.find(item => item.id === product.id && item.name === product.name);
      if (existing) {
        return prev.map(item => 
          (item.id === product.id && item.name === product.name)
            ? { ...item, quantity: Math.min(item.stock, item.quantity + qty) } 
            : item
        );
      }
      return [...prev, { ...product, quantity: Math.min(product.stock, qty) }];
    });
    track(subdomain, 'add_to_cart', { productId: product.id });
    
    // Dispatch event for themes (like Maison) to show header cart popover
    window.dispatchEvent(new CustomEvent('dilzee_cart_added'));

    if (openDrawer && !THEMES_WITH_CUSTOM_CART_UI.has(store?.active_theme || 'default')) {
      openCartDrawer();
    }
  };

  const removeFromCart = (productId: string, name?: string) => {
    applyCart(prev => prev.filter(item => !(item.id === productId && (!name || item.name === name))));
  };

  const updateQty = (productId: string, qty: number, name?: string) => {
    applyCart(prev => prev.map(item => {
      if (item.id === productId && (!name || item.name === name)) {
        return { ...item, quantity: Math.max(1, Math.min(item.stock, qty)) };
      }
      return item;
    }));
  };

  const clearCart = () => {
    applyCart(() => []);
  };

  const cartCount = displayCart.reduce((acc, item) => acc + item.quantity, 0);
  
  const cartTotal = displayCart.reduce((acc, item) => acc + (item.price * item.quantity), 0);
  
  const cartGst = displayCart.reduce((acc, item) => {
    const rate = typeof item.gst_rate === 'number' ? item.gst_rate : 18;
    return acc + Math.round(item.price * item.quantity * (rate / 100));
  }, 0);

  const isPlatformDomain = window.location.hostname.includes('pages.dev') || window.location.hostname === 'localhost' || window.location.hostname === 'store.dilzee.com';
  const linkTo = (path: string) => {
    if (!isPlatformDomain) return path;
    const connector = path.includes('?') ? '&' : '?';
    return `${path}${connector}store=${subdomain}`;
  };

  return (
    <StoreContext.Provider value={{
      store,
      products,
      collections,
      collection_products: collectionProducts,
      storePages,
      cart: displayCart,
      loading,
      error,
      addToCart,
      removeFromCart,
      updateQty,
      clearCart,
      cartTotal,
      cartCount,
      cartGst,
      subdomain,
      settings: store?.settings || {},
      affiliateCode: sessionStorage.getItem('dilzee_aff_code') || '',
      affiliateSession: sessionStorage.getItem('dilzee_aff_session') || '',
      linkTo,
      cartDrawerOpen,
      openCartDrawer,
      closeCartDrawer,
      quickViewProduct,
      setQuickViewProduct,
      sectionOrder,
      setSectionOrder
    }}>
      {children}
    </StoreContext.Provider>
  );
};
